import { useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
} from 'chart.js';
import { format, subMonths } from 'date-fns';
import { ExportToCsv } from 'export-to-csv';
import { Chart } from 'react-chartjs-2';
import { useQuery } from 'react-query';
import Subtitle from 'src/components/Subtitle';
import { getFiagroLiabilityData } from 'src/services/operation/operationFiagro';
import { FiagroLiabilityRaw } from 'src/types/operations/fiagro';
import { convertValue, toCurrency } from 'src/utils/number';

import csvSvg from '../../../../../assets/imgs/csv.svg';
import {
  formatCompetence,
  formatDateShort,
  parseScientificToNumber,
} from '../utils';

ChartJS.register(CategoryScale, LinearScale, BarElement, Legend, Tooltip);

type PeriodFilter = 12 | 60 | 0;

const PERIOD_OPTIONS: { key: PeriodFilter; label: string }[] = [
  { key: 12, label: '12 meses' },
  { key: 60, label: '60 meses' },
  { key: 0, label: 'Ótimo' },
];

const ACTIVE_BORDER = '2px solid #FF8211';

interface LiabilityPatrimonyProps {
  hash: string;
}

function resolvePatrimonyValue(row: FiagroLiabilityRaw): number | null {
  const pl = parseScientificToNumber(row.patrimonioLiquido);
  if (pl != null && pl !== 0) return pl;

  const vp = parseScientificToNumber(row.valorPassivo);
  if (vp != null && vp !== 0) return vp;

  const tp = parseScientificToNumber(row.totalPassivo);
  if (tp != null && tp !== 0) return tp;

  return pl ?? vp ?? tp;
}

export default function LiabilityPatrimony({ hash }: LiabilityPatrimonyProps) {
  const [isTableView, setIsTableView] = useState(false);
  const [periodFilter, setPeriodFilter] = useState<PeriodFilter>(0);
  const [tablePage, setTablePage] = useState(0);
  const [tableRowsPerPage, setTableRowsPerPage] = useState(10);

  const startDate =
    periodFilter !== 0
      ? format(subMonths(new Date(), periodFilter), 'yyyy-MM-dd')
      : undefined;

  const { data: rawData = [], isLoading } = useQuery({
    queryKey: ['operationFIAGROLiabilityPatrimony', hash, periodFilter],
    queryFn: () => getFiagroLiabilityData(hash, 1000, startDate),
    enabled: !!hash,
  });

  const sortedDesc = [...rawData].sort((a, b) =>
    b.dataReferencia.localeCompare(a.dataReferencia),
  );

  const sortedAsc = [...rawData].sort((a, b) =>
    a.dataReferencia.localeCompare(b.dataReferencia),
  );

  const handleExport = () => {
    const exporter = new ExportToCsv({
      fieldSeparator: ';',
      filename: 'fiagro_passivo_patrimonio',
      showLabels: true,
      useBom: true,
      useKeysAsHeaders: false,
      headers: ['Data', 'Patrimônio Líquido'],
    });

    exporter.generateCsv(
      sortedDesc.map((item) => ({
        data: formatCompetence(item.dataReferencia),
        patrimonioLiquido:
          resolvePatrimonyValue(item) != null
            ? toCurrency(resolvePatrimonyValue(item))
            : 'N/D',
      })),
    );
  };

  const chartOptions: any = {
    plugins: {
      legend: { display: true, onClick: () => {} },
      tooltip: {
        callbacks: {
          label: (tooltipItem: { label: string; raw: unknown }) =>
            `${tooltipItem.label}: ${convertValue(
              (tooltipItem.raw as number) ?? 0,
            )}`,
        },
      },
    },
    scales: {
      y: {
        ticks: { callback: (value: number) => convertValue(value) },
      },
    },
    maintainAspectRatio: false,
  };

  if (isLoading) {
    return (
      <Box>
        <Skeleton
          variant="text"
          width={150}
          height={20}
          sx={{ mb: 1, mt: 3 }}
        />
        <Box sx={{ height: 2, backgroundColor: 'black', mb: 2 }} />
        <Skeleton variant="rectangular" width="100%" height={520} />
      </Box>
    );
  }

  if (!rawData.length) {
    return (
      <>
        <Subtitle title="Patrimônio" />
        <Alert severity="info">
          Nenhum histórico de patrimônio disponível.
        </Alert>
      </>
    );
  }

  return (
    <>
      <Subtitle
        title="Patrimônio"
        hasSelectType
        isSelectedType={isTableView}
        handleChangeType={() => setIsTableView((prev) => !prev)}
        titleType1="Gráfico"
        titleType2="Tabela"
      />

      <Paper square sx={{ padding: '24px 18px', mb: 4 }}>
        {!isTableView && (
          <>
            <Box display="flex" gap={1} justifyContent="flex-end" mb={1}>
              {PERIOD_OPTIONS.map(({ key, label }) => (
                <Typography
                  key={key}
                  variant="button"
                  onClick={() => {
                    setPeriodFilter(key);
                    setTablePage(0);
                  }}
                  sx={{
                    cursor: 'pointer',
                    borderBottom:
                      periodFilter === key
                        ? ACTIVE_BORDER
                        : '2px solid transparent',
                    textTransform: 'none',
                  }}>
                  {label}
                </Typography>
              ))}
            </Box>

            <Box sx={{ height: 520 }}>
              <Chart
                type="bar"
                data={{
                  labels: sortedAsc.map((item) =>
                    formatDateShort(item.dataReferencia),
                  ),
                  datasets: [
                    {
                      type: 'bar',
                      label: 'Patrimônio Líquido',
                      data: sortedAsc.map(
                        (item) => resolvePatrimonyValue(item) ?? 0,
                      ),
                      backgroundColor: '#FF8211',
                    },
                  ],
                }}
                options={chartOptions}
              />
            </Box>
          </>
        )}

        {isTableView && (
          <>
            <Box display="flex" justifyContent="flex-end" mb={2}>
              <Button
                variant="contained"
                color="primary"
                sx={{ fontSize: '16px' }}
                startIcon={
                  <Box
                    component="img"
                    src={csvSvg}
                    alt="export"
                    sx={{ width: '20px', height: '20px' }}
                  />
                }
                onClick={handleExport}>
                Exportar
              </Button>
            </Box>

            <TableContainer>
              <Table size="medium">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, fontSize: '12px' }}>
                      Data
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: 600, fontSize: '12px' }}>
                      Patrimônio Líquido
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedDesc
                    .slice(
                      tablePage * tableRowsPerPage,
                      (tablePage + 1) * tableRowsPerPage,
                    )
                    .map((row) => (
                      <TableRow key={`${row.id}-${row.dataReferencia}`}>
                        <TableCell sx={{ fontSize: '0.8em' }}>
                          {formatCompetence(row.dataReferencia)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.8em' }}>
                          {resolvePatrimonyValue(row) != null
                            ? toCurrency(resolvePatrimonyValue(row))
                            : 'N/D'}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TablePagination
                      rowsPerPageOptions={[10, 30, 50]}
                      count={sortedDesc.length}
                      page={tablePage}
                      onPageChange={(_, newPage) => setTablePage(newPage)}
                      rowsPerPage={tableRowsPerPage}
                      onRowsPerPageChange={(event) => {
                        setTableRowsPerPage(parseInt(event.target.value, 10));
                        setTablePage(0);
                      }}
                      labelRowsPerPage="Registros por página"
                      labelDisplayedRows={({ from, to, count }) =>
                        `${from}-${to} de ${count}`
                      }
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </>
        )}
      </Paper>
    </>
  );
}
